import React from 'react'
import _ from 'lodash'
import { lookup as lookupTableColumn } from 'data/lib/lookup-field'
import styled, { css } from 'styled-components'
import { Colors } from 'styles/app-styles'
import { formatDate, isValidDate } from 'data/lib/dates'
import { local } from 'data/lib/local-json'

export function parseErrors(errordetail) {
  let errors = _.flatMapDeep(errordetail, (errsById, resource) => {
    return _.map(errsById, (errors, id) => {
      return _(errors)
        .map(err => {
          let { path, value, message, tags, type } = err
          err.errpath = [resource, id, ...path].join('.')
          let { code, suite, sortorder: row, unitdescription, description, cashflowlineitem, accountnumber, summaryaccountcode, email } = err._object
          let { section, field } = lookupTableColumn(resource, path[0], err._object)

          if (section == 'Multifamily Rent Roll') {
            code = unitdescription
          }

          if (section == 'Room') {
            code = description
          }

          if (section == 'Line Item' && _.includes(['hosp_dept_revenue', 'hosp_dept_expense'], _.get(err, '_object.type'))) {
            section = 'Departmental'
          }

          if (section == 'Cash Flow Map') {
            code = cashflowlineitem
            if (path[0] == 'summaryaccountid') value = summaryaccountcode
          }

          if (section == 'Summary Account' || section == 'Detail Account') {
            code = accountnumber
          }

          // Credit and Vacancy Loss have their own UI views but are actually on the property row.
          if (section == 'Property') {
            switch(path[0]) {
              case 'vacancylossdetail':
                section = 'Vacancy Loss'
                code = err._object.vacancylossaccountnumber
                field = 'Input Type'
                break;
              case 'creditlossdetail':
                section = 'Credit Loss'
                code = err._object.creditlossaccountnumber
                field = 'Input Type'
                break;
            }
          }
          if (section == 'Folder') {
            switch(path[0]) {
              case 'reportbegindate':
                field = 'Report Begin'
                break;
              default:
                field = _.startCase(path[0])
                break;
            }
          }
          message = message.replace(/^"\w+" /, '')
          value = type.startsWith('date.') && isValidDate(value) ? formatDate(value, 'MM/DD/YYYY') : value
          value = _.isPlainObject(value) ? Symbol.for('Invalid') : _.isString(value) && value.length > 25 ? value.slice(0,25) + '…' : value
          code = code && code.length > 25 ? code.slice(0,25) + '…' : code
          return { code, suite, row, section, field, path, value, message, tags, type, unitdescription, email, error: err }
        })
        .value()
    })
  })
  errors = _.orderBy(errors, ['section', 'code', 'suite', 'unitdescription', 'row', 'field'])
  return errors
}

export function parseErrorsToText(errordetail) {
  return _(parseErrors(errordetail))
    .groupBy('section')
    .map((errors, section) => {
      let header = ['Column', 'Current Value', 'Message']
      switch (section) {
        case 'Rent Roll':
          header = ['Suite', 'Row', ...header]
          break
        case 'Multifamily Rent Roll':
          header = ['Unit', ...header]
          break
        case 'User Settings':
          header = ['Email', ...header]
          break
        default:
          header = ['Code', ...header]
      }
      const lines = [header, ..._.map(errors, error => {
        const { code, suite, row, field, path, value, message, email } = error
        const _value =
          _.isArray(value) ? '[Items]' :
          _.isSymbol(value) ? Symbol.keyFor(value) :
          value === 0 ? 0 : value || '[Empty]'
        const codecol = section == 'Rent Roll' ? [suite || '[Empty]', row || ''] : [code || email || '[Empty]']
        const subfield = parseColumnName(section, [field, ...path.slice(1)])
        return [...codecol, subfield, _value, message]
      })]

      let widths = []
      for (const lix in lines) {
        for (const cix in lines[lix]) {
          const col = lines[lix][cix]
          if (!widths[cix] || widths[cix] < String(col).length) widths[cix] =  String(col).length
        }
      }

      for (const lix in lines) {
        for (const cix in lines[lix]) {
          lines[lix][cix] = _.padEnd(lines[lix][cix], widths[cix]+4)
        }
      }

      return ['', section, ...lines.map(line => line.join(''))].join('\r\n')
    })
    .value()
}

const TrErrorable = styled.tr`
${({severe}) => severe && css`
  background-color: ${Colors.colorWarning} !important;
`}
`
export function parseErrorsToErrorTable(errordetail, calcDetail) {
  let asObject = null
  const items = _(parseErrors(errordetail))
    .orderBy(['section', 'code', 'suite', 'row', 'field'])
    .map((error, ix) => {
      const { code, suite, row, field, path, section, value, message, tags, type, unitdescription, description, email } = error
      const codeText =
        section == 'Rent Roll'
        ? (suite ? (suite  + (row ? ` (Row ${row})` : '')) : <em>Empty</em>)
        : code || unitdescription || description || email || <em>Empty</em>
      const subfield = parseColumnName(section, [field || path[0], ...path.slice(1)])
      return {tags, type, section, code:codeText, column: subfield, value, message, path, error}
    })
    .orderBy(({tags,type}) => _.includes(tags, `${type}:severe`), 'desc')
    .tap(v => asObject = v)
    .map(({tags, type, section, code:codeText, column: subfield, value, message, error}, ix) => {
      const _value =
        _.isArray(value) ? <em>Items</em> :
        _.isSymbol(value) ? <em>{Symbol.keyFor(value)}</em> :
        value === 0 ? 0 : value || <em>Empty</em>
      return (
        <TrErrorable key={`validation-error-${ix}`} severe={_.includes(tags, `${type}:severe`)}>
          { section !== 'Organization' &&
            <td>
              <a href={getUrlFrom(error)}>{section}</a>
            </td>
          }
          { section !== 'Organization' &&
            <td>
              {codeText}
            </td>
          }
          <td>
            <span>{subfield}</span>
          </td>
          <td>
            {_value}
          </td>
          <td>
            {message}
          </td>
      </TrErrorable>
      )
    })
    .value()

  let codeHeader = 'Code'
  if (errordetail?.rentroll) codeHeader += '/Suite'
  if (errordetail?.user) codeHeader += '/Email'

  let header = (
    <thead>
      <tr>
        <th>Location</th>
        <th>{codeHeader}</th>
        <th>Column</th>
        <th>Current Value</th>
        <th>Message</th>
      </tr>
    </thead>
  )

  if (errordetail?.organization){
    header = (
      <thead>
        <tr>
          <th>Field</th>
          <th>Current Value</th>
          <th>Message</th>
        </tr>
      </thead>
    )
  }

  const calcItems = {
    items: calcDetail ? _.map(calcDetail.errors, it => <tr><td>{it.error}</td></tr>) : [],
    header: (
      <thead>
        <tr>
          <th>Calculation Warnings</th>
        </tr>
      </thead>
    )
  }
  return {header, items, calcItems, asObject}
}

const ExtraNamesByFieldPath = {
  Recovery: {
    amtItems: 'Amt',
    detailedAmtItems: 'Detailed Amt',
    namedRecoveryGroupItems: 'Recovery Group',
    namedRecoveryItems: 'Named Recovery',
    prorataRecoveryItems: 'Pro rata',
    tenantMgmtFeeItems: 'Tenant Mgmt Fee',
  },
  'Named Recovery': {
    proRataPercent: 'Pro rata %'
  },
  'Rent Roll': {
    prorataRecoveryItems: 'Pro rata'
  },
  'Market Lease': {
    prorataRecoveryItems: 'Pro rata'
  },
  'Valuation Settings': {
    abatementAdjustment: 'Gross-up Reversion Abatement',
    Valuationdetail: 'Valuation Scenario',
    vacancyLossAdjustment: 'Stabilize Reversion Vacancy Loss'
  },
  // Mutlifamily WIP; mapping currently unknown
  'Multifamily Rent Roll': {
    creditlossdetail: 'Credit Loss',
    dateMode: 'Date Mode',
    'Unit Inventory': {
      unitsOccupied: 'Units Occupied',
      unitsTotal: 'Units Total',
      unitsVacant: 'Units Vacant',
    }
  },
  'Property': {
    creditlossaccountnumber: 'Credit Loss Account Number',
    vacancylossaccountnumber: 'Vacancy Loss Account Number',
  }
}

const parseColumnName = (section, path) => {
  const special = _.get(ExtraNamesByFieldPath, [section, ...path])
  if (_.isString(special)) return special

  const subfield = _(path).map(s => _parseColumnName(s, section)).reduce((subfield, segment, ix, path) => {
    if (_.isFinite(+segment)) return `${subfield} ${+segment + 1}`
    if (section === 'Organization') return `${segment}`
    return ix == 0 ? segment : ix == 1 ? `${subfield}: ${segment}` : `${subfield} > ${segment}`
  },'')
  return subfield
}

const _parseColumnName = (name, section) => {
  {
    const special = _.get(ExtraNamesByFieldPath, [section, name])
    if (_.isString(special)) return special
  }
  if(name == 'items') name = 'Row'
  if(_.endsWith(name, 'Items')) name = name.replace(/Items$/,'')
  if(_.endsWith(name, 'NamedItemId')) name = name.replace(/NamedItemId$/,'')
  if(_.endsWith(name, 'Mode')) name = name.replace(/Mode$/,'')
  if(_.startsWith(name, 'named') && name != 'namedRecovery') name = name.replace(/^named/,'')
  return _.startCase(name)
}

export function filterErrors(errors){
  const verbose = local('verboseValidationErrors')

  errors = _.cloneDeep(errors)
  for (let table in errors) {
    for (let rowid in errors[table]) {
      const rowErrors = _.filter(errors[table][rowid], isErrorVisible)
      if(_.isEmpty(rowErrors))
        delete errors[table][rowid]
      else
        errors[table][rowid] = rowErrors
    }
    if (_.isEmpty(errors[table]))
      delete errors[table]
  }
  return _.isEmpty(errors) ? null : errors

  function isErrorVisible({type, tags}){
    return verbose || !_.includes(tags, `${type}:internal`) && !_.includes(tags, `*:internal`)
  }
}

export function propertyHasErrors(property) {
  let errordetail = null;
  if (property) {
    errordetail = filterErrors(property['errordetail'], JSON.parse(localStorage.getItem('verboseValidationErrors')));
  }
  return !!errordetail
}

const pathUrlMap = {
  // _: '/n/asset-management/explorer',
  // _: '/n/asset-management/asset-list',
  // _: '/n/asset-management/asset-list/:assetId',
  // _: '/n/asset-management/attributes',
  // _: '/n/asset-management/portfolios',
  // _: '/n/asset-management/portfolios/:portfolioId',
  // _: '/n/asset-management/chart',
  // _: '/n/asset-management/comparison',
  // _: '/n/asset-management/comparison/:comparisonId',
  // _: '/n/asset-management/chart/:chartId/summary-accounts',
  // _: '/n/asset-management/chart/:chartId/detail-accounts',
  // _: '/n/asset-management/chart/:chartId/cash-flow-map',
  // _: '/n/asset-management/reports',
  // _: '/n/asset-management/audit-log',
  'room': '/n/hospitality/rooms',
  // _: '/n/hospitality/line-items',
  // _: '/n/hospitality/departmental-line-items',
  // _: '/n/hospitality/area-measure',
  // _: '/n/hospitality/inflation',
  'multifamily': '/n/multifamily/rent-roll',
  // _: '/n/multifamily/area-measure',
  // _: '/n/multifamily/line-items',
  // _: '/n/multifamily/inflation',
  // _: '/n/folder/explorer',
  'folder': '/n/folder/settings',
  // _: '/n/folder/underwriting-workbook',
  // _: '/n/folder/property-list',
  // _: '/n/folder/reports',
  // _: '/n/folder/snapshots',
  // _: '/n/folder/import-export',
  // _: '/n/folder/audit-log',
  // _: '/n/property/asset-info/:assetId',
  'area': '/n/property/area-measure',
  // _: '/n/property/asset-attributes',
  'property': '/n/property/settings',
  'property.#.valuationdetail': '/n/property/valuation-settings',
  'property.#.*defaults': '/n/property/defaults',
  // _: '/n/property/snapshots',
  // _: '/n/property/import-export',
  'rentroll': '/n/rent-roll/home',
  'marketlease': '/n/rent-roll/market-lease',
  'recoverygroup': '/n/rent-roll/recovery-groups',
  'tenantgroup': '/n/rent-roll/tenant-groups',
  'recovery': '/n/rent-roll/named-recoveries',
  // _: '/n/mixed-use/mf-rent-roll',
  // _: '/n/mixed-use/line-items',
  // _: '/n/mixed-use/inflation',
  // _: '/n/mixed-use/area-measures',
  'lineitem': (error) => {
    switch(error.error._object.type){
      case 'expense': return '/n/line-items/expense'
      case 'expense_non_recov': return '/n/line-items/non-recoverable-expense'
      case 'hosp_dept_revenue': return '/n/hospitality/departmental-line-items'
      case 'building_capital': return '/n/line-items/building-capital'
      case 'hosp_dept_expense': return '/n/hospitality/departmental-line-items'
      case 'misc_income': return '/n/line-items/misc-income'
      default: return '/n/line-items/all'
    }
  },
  'inflation': '/n/property-market/inflation',
  // 'marketlease': '/n/property-market/market-lease',
  'downtime': '/n/property-market/downtime',
  'renewalprobability': '/n/property-market/renewal-probability',
  'property.#.*lossdetail': '/n/property-market/vacancy-credit-loss',
  'rent': '/n/tenant-market/rent',
  'tenantcapital': (error) => {
    switch(error.error._object.type){
      case 'ti': return '/n/tenant-market/tenant-improvement'
      case 'lc': return '/n/tenant-market/leasing-commissions'
      case 'oc': return '/n/tenant-market/other-capital'
    }
  },
  'freerent': '/n/tenant-market/free-rent',
  'increase': '/n/tenant-market/increase',
  'payout': '/n/tenant-market/payout',
  // 'area': '/n/recoveries/area-measure',
  // 'recovery': '/n/recoveries/named-recoveries',
  // 'recoverypool': '/n/recoveries/recovery-pools',
  'recoverypool.#': (error) => {
    if (['code', 'name'].includes(error.path[0]))
      return '/n/recoveries/recovery-pools'
    return '/n/recoveries/recovery-pools/:recoveryPoolId'
  },
  'recoverygroup': '/n/recoveries/recovery-groups',
  'tenantcontribution': '/n/recoveries/tenant-contributions',
  // 'tenantgroup': '/n/recoveries/tenant-groups',
  'reporttag': '/n/recoveries/report-tags',
  // _: '/n/admin/users',
  // _: '/n/admin/user-groups',
  // _: '/n/admin/organization-settings',
  // _: '/n/admin/audit-log'
}
function getUrlFrom(error) {
  const errpath = error.error.errpath.split('.')
  const searchPaths = _(pathUrlMap).keys().map(s => _.split(s,'.')).sortBy(_.length).value()
  nextpath:
  for(let path of searchPaths){
    for(let [step, errstep] of _.zip(path, errpath)) {
      if (_.isNil(step)) break
      if (step == '#') {
        if (_.isFinite(+errstep)) continue
        continue nextpath
      }
      if (step.startsWith('*')) {
        if (errstep.endsWith(step.slice(1))) continue
        continue nextpath
      }
      if (step == errstep) continue
      continue nextpath
    }
    // fell through on longest match, so
    // lookup url by original path
    let url = pathUrlMap[path.join('.')]
    if (_.isFunction(url)) {
      url = url(error)
    }
    // parse :param if any
    url = url?.replace(/:\w+/, errpath[1])
    return url
  }
  console.warn('missing url for', errpath.join('.'))
}